import i18n from '../../../../../common/i18n';
import { FlowConnector, FlowItem, StepItem } from '../../../../_components/flow-builder/TypeDefs';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { getServiceTypeIcon } from '../../../../_components/journey-builder-footer/utility/Helpers';
import { journeyCanvasConstants, stepItemConfig } from '../../../../constants/JourneyCanvas';
import { CreateJourneyStepDetails, CreateJourneyStepEdge, CreateJourneyStepNode, ViewJourneyData, ViewJourneyEventMapDetails } from '../../../../store/_api/TypeDefs';
import { EventMapDetails, EventMapEdge, EventMapNode, FlowItemConditions, FlowItemDataMap, JourneyStepAction, PayloadTransformation, ServiceCommonData, ServiceTypes, StepJourneyData, StepJourneyDataMap, FlowConnectorButtonActions } from '../TypeDefs';
import { filterPayloadTransformationForConvergence, getMepValue } from './CanvasHelpers';
import { applyConnectorConditionalExpressionRules, type FlowItemMergeRef } from './AndMergeForkHelpers';
import { checkBusinessToBusinessConnector, generateTooltipTextForConnector, getFlowConnectorButtons, getFlowConnectorConfig } from './ConnectorsHelpers';
import { getFlowItemButtons, getFlowItemConfig } from './FlowItemsHelpers';
import { prepareStepConnectorFromData, prepareStepJourneyFromData } from './JourneyStepHelpers';

import stepConnectorIcon from '../../../../assets/images/AddStepIcon.svg';
import menuDotsIcon from '../../../../assets/images/MenuDots.png';
import cemCountIcon from '../../../../assets/images/StepCemCountIcon.svg';
import eventCountIcon from '../../../../assets/images/StepEventCountIcon.svg';
import stepIcon from '../../../../assets/images/StepIcon.svg';
import serviceCountIcon from '../../../../assets/images/StepServiceCountIcon.svg';

export const translateLabel = (key: string) => (i18n.t(key));

/**
 * @method prepareJourneyFromData
 * @description Prepares data for FlowBuilder to render the journey to canvas.
 * @param {ViewJourneyData} data - The view journey data returned from the API.
 * @param {boolean} readOnly - if canvas is in view mode
 * @returns {Object} - The prepared journey data.
 */
export const prepareJourneyFromData = (data: ViewJourneyData, readOnly: boolean, isCloneJourney: boolean, isDesigningCEM: boolean): {
  items: StepItem[],
  connectors: FlowConnector[],
  stepJourneyDataMap: StepJourneyDataMap,
  flowItemDataMap: FlowItemDataMap,
  payloadTransformations: PayloadTransformation[]
} => {
  const flowItemDataMap: FlowItemDataMap = {};
  const stepJourneyDataMap: StepJourneyDataMap = {};
  const stepItems: StepItem[] = [];
  const payloadTransformations: PayloadTransformation[] = [];
  data.journeyJsonDetails.stepNodes?.forEach((node) => {
    const stepJourneyData = prepareStepJourneyFromData({ data: node, flowItemDataMap, status: data.status, journeyStepDetails: data.stepDetails, stepEdges: data.journeyJsonDetails.stepEdges, readOnly, isCloneJourney, isDesigningCEM });
    if (!stepJourneyData) {
      return;
    }
    const stepJourney = stepJourneyData.step;
    payloadTransformations.push(...stepJourneyData.payloadTransformations);
    stepJourneyDataMap[node.stepNodeId] = stepJourney;
    stepItems.push({
      nodeId: node.stepNodeId,
      x: node.xCoordinate,
      y: node.yCoordinate,
      name: stepJourney.journeyStepName,
      description: stepJourney.journeyStepDescription,
      serviceCount: node.servicesCount,
      eventCount: node.eventsCount,
      cemCount: node.cemCount,
      serviceCountIcon,
      eventCountIcon,
      cemCountIcon,
      menuDotsIcon,
      buttons: [],
      stepIcon,
      stepConnectorIcon,
      isRoot: false,
      isStackedStyled: false,
      config: stepItemConfig
    });
  });

  const { connectors } = prepareStepConnectorFromData(data.journeyJsonDetails.stepEdges, flowItemDataMap, stepJourneyDataMap);

  return {
    items: stepItems,
    connectors,
    stepJourneyDataMap,
    flowItemDataMap,
    payloadTransformations
  };
};

/**
 * @method createJourneyStepNodes
 * @description Creates journey step nodes based on the provided data.
 * @param {Object} options - The options object.
 * @param {StepItem[]} options.stepItems - The step items.
 * @param {StepJourneyDataMap} options.stepData - The map of step journey data.
 * @param {FlowConnector[]} options.stepEdges - The step edges.
 * @param {FlowItemDataMap} options.itemDataMap - The map of flow item data.
 * @param {PayloadTransformation[]} options.payloadTransformation - The payload transformations.
 * @returns {Object} - The created journey step nodes.
 */
export const createJourneyStepNodes = ({
  stepItems,
  stepEdges,
  stepData,
}: {
  stepItems: StepItem[],
  stepEdges: FlowConnector[],
  stepData: StepJourneyDataMap,
}) => {
  const stepNodes: CreateJourneyStepNode[] = [];
  stepItems.forEach((item) => {
    const { nodeId } = item;
    const stepNodeData = stepData[nodeId];
    const nextStepId = stepEdges.find((e) => e.fromNodeId === item.nodeId)?.toNodeId;
    const nextStep = nextStepId ? stepData[nextStepId] : undefined;
    const isNextStepCreateOrDelete = [JourneyStepAction.CREATE, JourneyStepAction.DELETE, JourneyStepAction.UPDATE].includes(nextStep?.journeyStepAction ?? JourneyStepAction.NONE);
    stepNodes.push({
      stepName: stepNodeData.journeyStepName,
      stepNodeId: nodeId,
      stepDescription: stepNodeData.journeyStepDescription,
      stepAction: isNextStepCreateOrDelete && ![JourneyStepAction.CREATE, JourneyStepAction.DELETE].includes(stepNodeData.journeyStepAction) ? JourneyStepAction.UPDATE : stepNodeData.journeyStepAction, // if next step is created or deleted means current step also is updated
      servicesCount: item.serviceCount,
      eventsCount: item.eventCount,
      cemCount: item.cemCount,
      xCoordinate: item.x,
      yCoordinate: item.y,
    });
  });
  return {
    stepNodes
  };
};

/**
 * @method createJourneyStepDetails
 * @description Creates journey step details based on the provided data.
 * @param {Object} options - The options object.
 * @param {StepItem[]} options.stepItems - The step items.
 * @param {StepJourneyDataMap} options.stepData - The map of step journey data.
 * @param {FlowConnector[]} options.stepEdges - The step edges.
 * @param {FlowItemDataMap} options.itemDataMap - The map of flow item data.
 * @param {PayloadTransformation[]} options.payloadTransformation - The payload transformations.
 * @returns {Object} - The created journey step details.
 */
export const createJourneyStepDetails = ({
  cloneJourneyName,
  stepItems,
  stepData,
  itemDataMap,
  stepEdges,
  payloadTransformation
}: {
  cloneJourneyName: string
  stepItems: StepItem[],
  stepData: StepJourneyDataMap,
  itemDataMap: FlowItemDataMap,
  stepEdges: FlowConnector[],
  payloadTransformation: PayloadTransformation[]
}) => {
  const stepDetails: CreateJourneyStepDetails = {};
  stepItems.forEach((item) => {
    const { nodeId: stepNodeId, name: stepName } = item;
    const stepNodeData = stepData[stepNodeId];
    const items = stepNodeData.flowItems;
    const connectors = stepNodeData.flowConnectors;
    const nextStepId = stepEdges.find((e) => e.fromNodeId === stepNodeId)?.toNodeId;
    const nextStep = nextStepId ? stepData[nextStepId] : undefined;
    const { nodes } = createEventMapNode(items, itemDataMap, nextStep);
    const { edges } = createEventMapEdge(connectors, items, itemDataMap, payloadTransformation, stepNodeData.startingNode?.nodeId, nextStep);
    // for very first step first node is ingressNodeId but for subsequent steps it's startingNode
    const startNode = stepNodeData.startingNode ?? stepNodeData.flowItems.find((i) => itemDataMap[i.nodeId].isExternal && i.nodeId === stepNodeData.ingressNodeId);
    const startNodeData = startNode ? itemDataMap[startNode.nodeId] : undefined;
    stepDetails[stepName] = {
      eventMapMaxExecutionTimeInSec: stepNodeData.maxExecutionTime === 0 ? null : stepNodeData.maxExecutionTime,
      uniqueIdentifier: !cloneJourneyName && stepNodeData.uniqueIdentifiers?.length ? stepNodeData.uniqueIdentifiers.toString() : undefined,
      startNode: (startNode && startNodeData) ? {
        nodeId: startNodeData.nodeId,
        serviceDescription: startNodeData.serviceDescription,
        serviceName: startNodeData.serviceName,
        serviceOperationName: startNodeData.serviceOperationName,
        serviceType: startNodeData.serviceType,
        eventName: startNodeData.ingressEvent,
        eventVersion: startNodeData.ingressEventVersion,
        serviceVersion: startNodeData.serviceVersion,
        xCoordinate: startNode.x,
        yCoordinate: startNode.y,
      } : undefined,
      eventMapNodes: nodes,
      eventMapEdges: edges,
      eventMapName: stepNodeData.eventMapName,
    };
  });
  return stepDetails;
};

/**
 * @method createJourneyStepEdgeData
 * @description Creates journey step edge data based on the provided edges and connector.
 * @param {JourneyStepEdge[]} edges - The existing journey step edges.
 * @param {FlowConnector} connector - The connector to be added.
 */
export const createJourneyStepEdgeData = (edges: CreateJourneyStepEdge[], connector: FlowConnector) => {
  // check if the connector's from node already exists in an edge as ingress
  const existingIngress = edges.find((e) => e.stepIngress.length === 1 && e.stepIngress[0].stepNodeId === connector.fromNodeId);
  // check if the connector's to node already exists in an edge as egress
  const existingEgress = edges.find((e) => e.stepEgress.find((eg) => eg.stepNodeId === connector.toNodeId));
  if (existingIngress) {
    // if from node exists as ingress then add the to node to same edge egress list
    existingIngress.stepEgress.push({ stepNodeId: connector.toNodeId, eventName: connector.text });
  }
  if (existingEgress) {
    // if to node exists as egress in an edge
    if (existingEgress.stepEgress.length === 1) {
      // if edge has only 1 egress then add the from node to ingress
      existingEgress.stepIngress.push({ stepNodeId: connector.fromNodeId });
    } else {
      edges.push({
        stepIngress: [...existingEgress.stepIngress, { stepNodeId: connector.fromNodeId }],
        stepEgress: [{ stepNodeId: connector.toNodeId, eventName: connector.text }],
      });
    }
  }
  if (!existingIngress && !existingEgress) {
    // connector don't exist in any edge create a new one
    edges.push({
      stepIngress: [{ stepNodeId: connector.fromNodeId }],
      stepEgress: [{ stepNodeId: connector.toNodeId, eventName: connector.text }]
    });
  }
};

/**
 * @method createJourneyStepEdges
 * @description Creates journey step edges based on the provided connectors.
 * @param {FlowConnector[]} connectors - The connectors.
 * @returns {Object} - The created journey step edges.
 */
export const createJourneyStepEdges = (connectors: FlowConnector[]) => {
  const stepEdges: CreateJourneyStepEdge[] = [];
  connectors.forEach((connector) => {
    if (connector.fromNodeId) {
      createJourneyStepEdgeData(stepEdges, connector);
    }
  });
  return {
    stepEdges
  };
};

/**
 * @method createEventMapDetail
 * @description Creates event map details based on the provided data.
 * @param {FlowItem[]} items - The flow items.
 * @param {FlowConnector[]} connectors - The connectors.
 * @param {FlowItemDataMap} itemDataMap - The map of flow item data.
 * @param {number} maxExecTime - The maximum execution time.
 * @param {string | undefined} tags - The unique identifier tags.
 * @returns {Object} - The created event map details.
 */
export const createEventMapDetail = (
  items: FlowItem[],
  connectors: FlowConnector[],
  itemDataMap: FlowItemDataMap,
  maxExecTime: number,
  tags: string | undefined,
  payloadTransformation: PayloadTransformation[] = [],
): EventMapDetails => {
  let nodes: EventMapNode[] = [];
  let edges: EventMapEdge[] = [];
  const eventMapMaxExecutionTimeInSec = maxExecTime === 0 ? undefined : maxExecTime;
  const uniqueIdentifier = tags !== '' ? tags : undefined;
  const newNode = createEventMapNode(items, itemDataMap);
  nodes = newNode.nodes;

  const newEdge = createEventMapEdge(connectors, items, itemDataMap, payloadTransformation);
  edges = newEdge.edges;

  return {
    eventMapNodes: nodes,
    eventMapEdges: edges,
    eventMapMaxExecutionTimeInSec,
    uniqueIdentifier
  };
};

/**
 * @method createEventMapNode
 * @description Creates event map nodes based on the provided data.
 * @param {FlowItem[]} items - The flow items.
 * @param {FlowItemDataMap} itemDataMap - The map of flow item data.
 * @returns {Object} - The created event map nodes.
 */
const createEventMapNode = (items: FlowItem[], itemDataMap: FlowItemDataMap, nextStepData: StepJourneyData | undefined = undefined) => {
  const nodes: EventMapNode[] = [];
  items.forEach((item) => {
    const itemData = itemDataMap[item.nodeId];
    if (itemData && (!item.isRoot || !itemData.isExternal)) {
      // shouldn't include in node list if root and external
      nodes.push({
        nodeId: item.nodeId,
        eventName: itemData.ingressEvent,
        status: journeyCanvasConstants.defaultEventMapEventStatus,
        serviceName: itemData.serviceName,
        serviceOperationName: itemData.serviceOperationName,
        serviceDescription: itemData.serviceDescription,
        serviceType: itemData.serviceType,
        xCoordinate: item.x,
        yCoordinate: item.y,
        isCompensateAction: itemData.isCompensateAction,
        compensatoryEventMapName: itemData.compensatoryEventMapName,
        serviceVersion: itemData.serviceVersion,
        eventVersion: itemData.ingressEventVersion
      });
    }
  });
  // if next step is there add the additional node with partial data
  if (nextStepData?.startingNode && nextStepData?.journeyStepAction !== JourneyStepAction.DELETE) {
    const rootNodeId = nextStepData.flowConnectors.find((fc) => fc.fromNodeId === nextStepData.startingNode?.nodeId)?.toNodeId;
    if (rootNodeId) {
      const rootNodeData = itemDataMap[rootNodeId];
      // all the fields are not required, read will skip this node
      nodes.push({
        nodeId: rootNodeId,
        eventName: rootNodeData.ingressEvent,
        eventVersion: rootNodeData.ingressEventVersion,
      } as unknown as EventMapNode);
    }
  }

  return {
    nodes
  };
};

/**
 * @method createEventMapEdgeData
 * @description Creates event map edge data based on the provided data.
 * @param {Object} options - The options object.
 * @param {EventMapEdge[]} options.edges - The existing event map edges.
 * @param {FlowConnector} options.connector - The connector to be added.
 * @param {ServiceCommonData} options.fromNodeData - The service data of the from node.
 * @param {ServiceCommonData} options.toNodeData - The service data of the to node.
 * @param {FlowItem} options.toNode - The to node.
 * @param {FlowItem[]} options.startingNodes - The starting nodes.
 * @param {PayloadTransformation[]} options.payloadTransformation - The payload transformations.
 */
export const createEventMapEdgeData = ({
  edges,
  connector,
  toNodeData,
  toNode,
  startingNodeId,
  payloadTransformation,
  externalValue
}: {
  edges: EventMapEdge[],
  connector: FlowConnector,
  toNodeData: ServiceCommonData,
  toNode: FlowItem,
  startingNodeId: string,
  payloadTransformation: PayloadTransformation[],
  externalValue: boolean
}) => {
  // check if the connector's to node already exists in an edge as egress
  const existingEgress = edges.find((e) => e.egress.find((eg) => eg.nodeId === connector.toNodeId));
  if (startingNodeId === connector.fromNodeId) {
    // skip this edge as it starts from a starting node
    return;
  }

  if (existingEgress) {
    // if to node exists as egress in an edge
    if (existingEgress.egress.length === 1) {
      // if edge has only 1 egress then add the from node to ingress
      existingEgress.ingress.push({ nodeId: connector.fromNodeId });
      existingEgress.ingressOperation = toNode.conditionText;
      console.log('[API EdgeData] toNodeId:', connector.toNodeId, '| ingressOperation:', toNode.conditionText);
      existingEgress.payloadTransformations = filterPayloadTransformationForConvergence(
        payloadTransformation,
        existingEgress.ingress.map((i) => i.nodeId),
        existingEgress.egress.map((e) => e.nodeId),
        existingEgress.ingressOperation
      );
      return;
    }
    // find if we already have a edge for convergence
    const existingConvergence = edges.find((e) => e.egress.length === 1 && e.egress[0].nodeId === connector.toNodeId);
    if (existingConvergence) {
      // convergence already exists add the from node to ingress list
      existingConvergence.ingress.push({ nodeId: connector.fromNodeId });
    } else {
      // convergence don't exists create a new one
      edges.push({
        ingress: [...existingEgress.ingress, { nodeId: connector.fromNodeId }],
        egress: [{
          nodeId: connector.toNodeId,
          mep: getMepValue(toNodeData.isExternal),
          external: externalValue,
          conditionMap: Object.keys(toNodeData.condExpr).length > 0 ? toNodeData.condExpr : undefined,
        }],
        ingressOperation: toNode.conditionText,
        payloadTransformations: filterPayloadTransformationForConvergence(
          payloadTransformation,
          [...existingEgress.ingress.map((i) => i.nodeId), connector.fromNodeId],
          [connector.toNodeId],
          toNode.conditionText
        ),
      });
    }
    return;
  }
  // connector don't exist in any edge create a new one
  edges.push({
    ingress: [{ nodeId: connector.fromNodeId }],
    egress: [{
      nodeId: connector.toNodeId,
      mep: getMepValue(toNodeData.isExternal),
      external: externalValue,
      conditionMap: Object.keys(toNodeData.condExpr).length > 0 ? toNodeData.condExpr : undefined,
    }]
  });
};

/**
 * @method createEventMapEdge
 * @description Creates event map edges based on the provided data.
 * @param {FlowConnector[]} connectors - The connectors.
 * @param {FlowItem[]} items - The flow items.
 * @param {FlowItemDataMap} itemDataMap - The map of flow item data.
 * @returns {Object} - The created event map edges.
 */
export const createEventMapEdge = (connectors: FlowConnector[], items: FlowItem[], itemDataMap: FlowItemDataMap, payloadTransformation: PayloadTransformation[] = [], startingNodeId = '', nextStepData: StepJourneyData | undefined = undefined) => {
  const edges: EventMapEdge[] = [];
  connectors.forEach((connector) => {
    const item = items.find((i) => i.nodeId === connector.fromNodeId);
    if (connector.fromNodeId && (!item?.isRoot || !itemDataMap[connector.fromNodeId].isExternal)) {
      const fromNodeData = itemDataMap[connector.fromNodeId];
      const toNodeData = itemDataMap[connector.toNodeId];
      const toNode = items.find((i) => i.nodeId === connector.toNodeId);
      const fromNode = items.find((i) => i.nodeId === connector.fromNodeId);

      if (fromNodeData && toNodeData && toNode && fromNode) {
        const externalValue = itemDataMap[toNodeData.nodeId]?.external[`${fromNodeData.nodeId}_${toNodeData.nodeId}`] ?? false;
        createEventMapEdgeData({ edges, connector, toNodeData, toNode, startingNodeId, payloadTransformation, externalValue });
      }
    }
  });
  console.log('[API Edges] edges with ingressOperation:', edges.map((e) => ({ egress: e.egress.map((eg) => eg.nodeId), ingressOperation: e.ingressOperation })));
  // add the additional edge with nextStep
  if (nextStepData?.startingNode && nextStepData?.journeyStepAction !== JourneyStepAction.DELETE) {
    const rootNodeId = nextStepData.flowConnectors.find((fc) => fc.fromNodeId === nextStepData.startingNode?.nodeId)?.toNodeId;
    if (rootNodeId) {
      const rootNodeData = itemDataMap[rootNodeId];
      const startingNodeData = itemDataMap[nextStepData.startingNode.nodeId];
      edges.push({
        ingress: [{ nodeId: nextStepData.startingNode.nodeId }],
        egress: [{
          nodeId: rootNodeId,
          external: startingNodeData.isExternal,
          mep: getMepValue(rootNodeData.isExternal),
          nextStep: nextStepData.journeyStepName,
        }]
      });
    }
  }

  return {
    edges
  };
};

/**
 * @method createEventMapNodes
 * @description Creates event map nodes based on the provided data.
 * @param {ViewJourneyEventMapDetails} data - The event map details.
 * @param {FlowItemDataMap} itemEventMap - The map of flow item data.
 * @param {boolean} readOnly - if canvas is in view mode
 * @returns {Object} - The created event map nodes.
 */
export const createEventMapNodes = (data: ViewJourneyEventMapDetails, itemEventMap: FlowItemDataMap, readOnly: boolean) => {
  const items: FlowItem[] = [];
  const flowItemMap: { [key: string]: FlowItem } = {};
  const egressCountMap: { [key: string]: number } = {};
  data.eventMapNodes?.forEach((node) => {
    // to skip the additional node from next step
    if (!node.serviceName) {
      return;
    }

    const isExternal = node.serviceType?.toLowerCase() === ServiceTypes.EXTERNAL;

    // Main journey flowItems
    const convergenceEdge = data.eventMapEdges?.find((edge) => edge.ingress.length > 0 && edge.egress.length === 1 && edge.egress[0].nodeId === node.nodeId);
    const conditionText = convergenceEdge?.ingressOperation;
    const item: FlowItem = {
      nodeId: node.nodeId,
      name: node.serviceName,
      description: node.serviceDescription ?? '',
      subTitle: node.serviceOperationName,
      icon: getServiceTypeIcon(node.serviceType),
      selectedIcon: getServiceTypeIcon(node.serviceType),
      isRoot: false,
      x: node.xCoordinate,
      y: node.yCoordinate,
      config: getFlowItemConfig({ isExternal, conditionText }),
      buttons: getFlowItemButtons(
        !!node.compensatoryEventMapName,
        !!node.isCompensateAction && !isExternal,
        conditionText === FlowItemConditions.AND || conditionText === FlowItemConditions.T_AND,
        readOnly
      ),
      isStackedStyled: !!node.compensatoryEventMapName,
      conditionText,
      displayVersion: node.serviceVersion,
      isPayloadTransformationDataSelected: (conditionText === FlowItemConditions.AND || conditionText === FlowItemConditions.T_AND) && !!convergenceEdge?.payloadTransformations?.length,
    };

    items.push(item);
    flowItemMap[node.nodeId] = item;
    itemEventMap[node.nodeId] = {
      nodeId: node.nodeId,
      serviceName: node.serviceName,
      ingressEvent: node.eventName,
      serviceOperationName: node.serviceOperationName,
      serviceDescription: node.serviceDescription ?? '',
      serviceType: node.serviceType ?? ServiceTypes.BUSINESS,
      isExternal,
      isCompensateAction: !!node.isCompensateAction && !isExternal,
      compensatoryEventMapName: node.compensatoryEventMapName,
      isSaved: true,
      serviceVersion: node.serviceVersion ?? 1,
      ingressEventVersion: node.eventVersion ?? 1,
      condExpr: {},
      external: {}
    };

    egressCountMap[node.nodeId] = 0;
  });

  return {
    items,
    flowItemMap,
    egressCountMap
  };
};

/** Merge-node AND/OR labels from event map (same convergence rule as createEventMapNodes). */
const mergeConditionRefsFromEventMap = (data: ViewJourneyEventMapDetails): FlowItemMergeRef[] =>
  (data.eventMapNodes ?? [])
    .filter((node) => !!node.serviceName)
    .map((node) => {
      const convergenceEdge = data.eventMapEdges?.find(
        (edge) => edge.ingress.length > 0 && edge.egress.length === 1 && edge.egress[0].nodeId === node.nodeId
      );
      return {
        nodeId: node.nodeId,
        conditionText: convergenceEdge?.ingressOperation,
      };
    });

/**
 * @method createEventMapEdges
 * @description Creates event map edges based on the provided data.
 * @param {ViewJourneyEventMapDetails} data - The event map details.
 * @param {FlowItemDataMap} itemEventMap - The map of flow item data.
 * @param {Object} egressCountMap - The egress count map.
 * @param {boolean} readOnly - if canvas is in view mode
 * @returns {Object} - The created event map edges.
 */
export const createEventMapEdges = (data: ViewJourneyEventMapDetails, itemEventMap: FlowItemDataMap, egressCountMap: { [key: string]: number }, readOnly: boolean, isDesigningCEM: boolean) => {
  const connectors: FlowConnector[] = [];
  const payloadTransformations: PayloadTransformation[] = [];
  data.eventMapEdges?.forEach((edge) => {
    if (edge.payloadTransformations) {
      payloadTransformations.push(...edge.payloadTransformations);
    }
    edge.ingress.forEach((i) => {
      edge.egress.forEach((e) => {
        if (itemEventMap[i.nodeId] && itemEventMap[e.nodeId] && !connectors.find((c) => c.fromNodeId === i.nodeId && c.toNodeId === e.nodeId)) {
          const convergingConnectors = connectors.filter((c) => c.toNodeId === e.nodeId);
          let convergence = false;

          // Detect convergence - if there are existing connectors pointing to the same target
          if (convergingConnectors.length > 0) {
            convergence = true;
            // Add delete buttons to ALL existing connectors that point to this target
            convergingConnectors.forEach((c) => {
              if (!c.buttons.find((b) => b.id === FlowConnectorButtonActions.DELETE)) {
                c.buttons = getFlowConnectorButtons(
                  true,
                  readOnly,
                  checkBusinessToBusinessConnector(itemEventMap[c.fromNodeId].serviceType, itemEventMap[e.nodeId].serviceType),
                  isDesigningCEM,
                  true
                );
              }
            });
          }

          egressCountMap[e.nodeId] += 1;

          connectors.push({
            fromNodeId: i.nodeId,
            toNodeId: e.nodeId,
            orientation: {
              from: Orientation.HORIZONTAL,
              to: Orientation.HORIZONTAL
            },
            text: itemEventMap[e.nodeId].ingressEvent,
            displayEventVersion: itemEventMap[e.nodeId].ingressEventVersion,
            tooltip: generateTooltipTextForConnector(itemEventMap[e.nodeId].ingressEvent, !!e.mep, !!e.external),
            config: getFlowConnectorConfig({
              hasCondExpr: !!e.conditionMap && !!e.conditionMap[i.nodeId],
              external: !!e.external,
              mep: !!e.mep,
            }),
            buttons: getFlowConnectorButtons(
              convergence,
              readOnly,
              checkBusinessToBusinessConnector(itemEventMap[i.nodeId].serviceType, itemEventMap[e.nodeId].serviceType),
              isDesigningCEM,
              true
            )
          });
          itemEventMap[e.nodeId].condExpr = e.conditionMap ?? {};
          itemEventMap[e.nodeId].external = { ...itemEventMap[e.nodeId].external, [`${i.nodeId}_${e.nodeId}`]: e.external };
        }
      });
    });
  });

  const andMergeNodeIds = new Set<string>();
  data.eventMapEdges?.forEach((edge) => {
    if (edge.ingressOperation === FlowItemConditions.AND && edge.egress?.[0]?.nodeId) {
      andMergeNodeIds.add(edge.egress[0].nodeId);
    }
  });
  const connectorsWithRules = applyConnectorConditionalExpressionRules(
    connectors,
    andMergeNodeIds,
    mergeConditionRefsFromEventMap(data),
    itemEventMap,
    readOnly,
    isDesigningCEM
  );

  return {
    connectors: connectorsWithRules,
    payloadTransformations,
  };
};

/**
 * @method findConnector
 * @description Finds a connector in the given array of connectors that matches the specified fromNodeId and toNodeId.
 * @param {FlowConnector[]} connectors - The array of connectors to search.
 * @param {string} fromNodeId - The fromNodeId to match.
 * @param {string} toNodeId - The toNodeId to match.
 * @returns {FlowConnector | undefined} - The matching connector, or undefined if not found.
 */
export const findConnector = (connectors: FlowConnector[], fromNodeId: string, toNodeId: string) => connectors.find((c) => c.fromNodeId === fromNodeId && c.toNodeId === toNodeId);
